import {useLazyQuery} from "@apollo/client";
import {useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";

import Button from "@/components/button/Button.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {Select} from "@/components/select/Select.tsx";
import {GQL_TELEGRAM_CHATS, TelegramChatsQueryResponse} from "@/lib/queries/integrations.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {TelegramChatList} from "@/pages/integrations/edit/subForms/telegramInfo.ts";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

const getPlaceholder = (telegramChatList: TelegramChatList) => {
  if (telegramChatList.telegramUsername.trim().length === 0) {
    return "";
  }

  if (telegramChatList.chats?.length === 0) {
    return `No chats found for '${telegramChatList.telegramUsername}'`;
  }

  return "Select Chat...";
};

interface TelegramFormProps {
  serverErrors?: ServerErrors;
}

export const TelegramForm = ({serverErrors}: TelegramFormProps) => {
  const {setError, setValue, watch} = useFormContext();
  const [telegramChatList, setTelegramChatList] = useState<TelegramChatList>({telegramUsername: "", chats: null});
  const [loadTelegramChatQuery] = useLazyQuery<TelegramChatsQueryResponse>(GQL_TELEGRAM_CHATS, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  const loadTelegramChats = async () => {
    const {data} = await loadTelegramChatQuery({variables: {username: watch("settings.telegram_username")}});

    setTelegramChatList({
      telegramUsername: watch("settings.telegram_username").replace("@", ""),
      chats: data!.telegramChats.items,
    });
  };

  return (
    <>
      <div className="pb-4">
        <p className="mb-2">
          Invite the bot <code>@UptimeDotComBot</code> to the group in which you want to receive notifications, then
          after supplying your username here, refresh the list of groups to select which one notifications will be sent
          to.
          <br />
          <br />
          Alternatively you can manually enter the Group ID below.
        </p>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3 d-flex gap-3 align-items-start ">
          <div className="w-100">
            <FormLabel forId="settings.telegram_username">Telegram Username</FormLabel>
            <InputField name="settings.telegram_username" autoFocus={true} />
          </div>
          <div>
            <FormLabel forId="settings.telegram_username">&nbsp;</FormLabel>
            <Button
              size="small"
              disabled={(watch("settings.telegram_username") || "").trim().length < 5}
              onClick={loadTelegramChats}
            >
              Load Chats
            </Button>
          </div>
        </div>
        <div className="col-6 mb-3">
          <div>
            <FormLabel forId="chat_list">Select Group</FormLabel>
            <Select
              isDisabled={telegramChatList.chats === null}
              placeholder={getPlaceholder(telegramChatList)}
              options={telegramChatList.chats || []}
              getOptionValue={(option) => option.chat_id}
              getOptionLabel={(option) => option.chat_title}
              onChange={(option) => {
                if (option) {
                  setValue("settings.chat_id", option.chat_id);
                  setValue("settings.chat_title", option.chat_title);
                }
              }}
            />
          </div>
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField name="settings.chat_id" label="Group ID" required={true} />
        </div>
        <div className="col-6 mb-3">
          <InputField name="settings.chat_title" label="Group Title" disabled={true} />
        </div>
      </div>
      <SetupInfo>
        <div className="p-2">
          The bot must have been invited in to your group within the last 24 hours in order to be visible in the above
          list.
          <br />
          <br />
          If the group is not showing up in the list, remove the bot user from the group and re-add it. This will not
          affect existing Telegram integrations.
        </div>
        <div className="p-2">
          <a target="_blank" href="https://support.uptime.com/hc/en-us/articles/23170619859612-Configuring-Telegram">
            View {IntegrationModuleInfo[IntegrationModules.TELEGRAM].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
